import type {
  CategoryResult,
  ClientCapabilities,
} from '../components/Reports/types/clientCategory';
import { isOnlineChannel, isPOSChannel } from '../components/Reports/utils/channelCategories';
import type { MenuSource } from '../components/RMS/rms.services';
import { useQueryRMSCommonDataHook } from './useQueryRMSCommonDataHook';
import type { SalesChannelType } from './useQueryRMSSalesChannels';

type Storefront = {
  code: string;
  type: string;
  menuSource: MenuSource;
  isArchived: boolean;
};

export function deriveCapabilities(storefronts: Storefront[]): ClientCapabilities {
  const active = storefronts.filter((s) => !s.isArchived);

  return {
    hasPOS: active.some((s) => isPOSChannel(s.type as SalesChannelType)),
    hasOnline: active.some((s) => isOnlineChannel(s.type as SalesChannelType)),
    hasRMSMenu: active.some((s) => s.menuSource === 'RMS' || s.menuSource === 'BOTH'),
  };
}

export function getClientCategory(capabilities: ClientCapabilities): CategoryResult {
  const { hasPOS, hasOnline, hasRMSMenu } = capabilities;

  if (!hasPOS && !hasOnline) {
    return { status: 'no_storefronts' };
  }

  if (hasPOS && hasOnline) {
    return { status: 'categorized', category: 'BOTH_ONLINE_AND_POS' };
  }

  if (hasPOS) {
    return { status: 'categorized', category: 'POS_ONLY' };
  }

  if (hasRMSMenu) {
    return { status: 'categorized', category: 'ONLINE_ONLY_RMS' };
  }

  return { status: 'categorized', category: 'ONLINE_ONLY_NON_RMS' };
}

export function useClientCategory(appId: string) {
  const { isLoading, data, error } = useQueryRMSCommonDataHook(appId);

  const properties = data?.associations?.orgHierarchy?.properties ?? [];
  const storefronts = properties.flatMap((property) => property.storefronts ?? []);
  const capabilities = deriveCapabilities(storefronts);
  const categoryResult = getClientCategory(capabilities);

  return {
    isLoading,
    error,
    categoryResult,
    capabilities,
    subscriptions: data?.subscriptions ?? [],
    menuSource: data?.associations?.menuSource,
  };
}
